import { forwardRef } from "react";
import { IconArrowUpRight } from "@web-monorepo/dds-icons";
import { BodyLevelClasses, type Variant, VariantClasses as BodyVariantClasses } from "../DDSBodyText/DDSBodyText";
import { LabelLevelClasses } from "../DDSLabelText/DDSLabelText";
import styles from "./DDSExternalLink.module.css";
/*
 * ExternalLink is a link that opens in a new tab with defense against the
 * window.opener vulnerability.
 *
 * By default it includes an external link icon (important for iKeepSafe Certification).
 * The icon is sized in em so it tracks the link's font-size — pass `level`
 * to lock both the text and the icon to a typography tier.
 *
 * You may set `showIcon={false}` when the destination allows the user to
 * easily access their account or return to their account via navigation
 * (e.g. teach, home, student, www.classdojo.com). Keep the icon for
 * external sites, help desk (Zendesk), and any ClassDojo pages where the
 * user cannot easily get back to their logged-in experience.
 */

const LEVEL_TO_CLASS = {
  "body-lg": BodyLevelClasses.bodyLg,
  "body-md": BodyLevelClasses.bodyMd,
  "body-sm": BodyLevelClasses.bodySm,
  "label-lg": LabelLevelClasses.labelLg,
  "label-md": LabelLevelClasses.labelMd,
  "label-sm": LabelLevelClasses.labelSm,
} as const;

type Level = keyof typeof LEVEL_TO_CLASS;

type DDSExternalLinkProps = JSX.IntrinsicElements["a"] & {
  /**
   * The name will get used for automated product events.
   * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
   */
  "data-name": string;
  /**
   * Typography tier applied to the link itself. Locks both the text and the
   * icon chip to the same font-size so they scale together. Prefer this over
   * wrapping the link's children in a `DDSBodyText` / `DDSLabelText`.
   */
  level?: Level;
  /**
   * Color variant applied to the link. Mirrors the variants on
   * `DDSBodyText` / `DDSLabelText`.
   */
  variant?: Variant;
  /**
   * Whether to show the external link icon. Defaults to true.
   * Set to false when the destination allows the user to easily access
   * their account or return to their account via navigation.
   * @see https://linear.app/classdojo/issue/DDS-18/reduce-overuse-of-external-link-icon-and-improve-styling
   */
  showIcon?: boolean;
  /**
   * @deprecated Use `showIcon={false}` instead.
   */
  dangerouslyOmitIcon?: boolean;
};

export const DDSExternalLink = forwardRef<HTMLAnchorElement, DDSExternalLinkProps>(
  ({ className, level, variant, showIcon, dangerouslyOmitIcon = false, ...props }, ref) => {
    const shouldShowIcon = showIcon ?? !dangerouslyOmitIcon;
    const classNames = [styles.link, level && LEVEL_TO_CLASS[level], variant && BodyVariantClasses[variant], className]
      .filter(Boolean)
      .join(" ");
    return (
      <a {...props} ref={ref} target="_blank" rel="noopener noreferrer" className={classNames}>
        {props.children}
        {shouldShowIcon && <IconArrowUpRight className={styles.icon} aria-hidden="true" />}
      </a>
    );
  },
);

DDSExternalLink.displayName = "DDSExternalLink";
